import { Link } from 'react-router-dom';
import { useState, type FormEvent } from 'react';
import { ErrorBox } from '../components/Feedback.js';
import { Preferencias } from '../components/Preferencias.js';
import { PageHeader } from '../components/PageHeader.js';
import {
  useAuth,
  useBottles,
  useCellars,
  useChangePassword,
  useExcluirConta,
  useLogout,
  usePasskeys,
  useRegistrarPasskey,
  useRemoverPasskey,
  passkeySuportado,
  useVersaoDaApi,
} from '../api/queries.js';
import { inicial, nomeFormatado } from '../lib/nome.js';
import { VERSAO_DO_APP, atualizarOApp } from '../lib/versao.js';

/** Tamanho mínimo da senha — o mesmo que a API exige. */
const MINIMO_SENHA = 8;

/**
 * Perfil: dados da conta, troca de senha e sair.
 *
 * Entrada pelo ícone com a inicial, no canto da tela inicial — sem aba nova na
 * barra de baixo, que já tem quatro itens no celular.
 */
export function Profile() {
  const { data: auth } = useAuth();
  const trocar = useChangePassword();
  const logout = useLogout();
  const versaoDaApi = useVersaoDaApi();
  const excluir = useExcluirConta();
  const { data: passkeys } = usePasskeys();
  const registrarPasskey = useRegistrarPasskey();
  const removerPasskey = useRemoverPasskey();
  // Para a confirmação falar em números, e não em "suas adegas e garrafas".
  const { data: adegas } = useCellars();
  const { data: garrafas } = useBottles({});

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [trocou, setTrocou] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);

  const contar = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;
  const resumo =
    adegas && garrafas
      ? `${contar(adegas.length, 'adega', 'adegas')} e ${contar(garrafas.length, 'garrafa', 'garrafas')}`
      : null;

  const usuario = auth?.user;
  if (!usuario) return null;

  // Só acusa desencontro com os dois números na mão: enquanto a API não
  // respondeu, não se sabe de nada.
  const versaoDaApiAgora = versaoDaApi.data?.version;
  const desencontroDeVersao =
    versaoDaApiAgora !== undefined && versaoDaApiAgora !== VERSAO_DO_APP;

  // Validação na hora, antes de enviar: o servidor valida de novo de qualquer forma.
  const problema =
    novaSenha.length > 0 && novaSenha.length < MINIMO_SENHA
      ? `A nova senha precisa de pelo menos ${MINIMO_SENHA} caracteres.`
      : confirmacao.length > 0 && confirmacao !== novaSenha
        ? 'A confirmação não é igual à nova senha.'
        : novaSenha.length > 0 && novaSenha === senhaAtual
          ? 'A nova senha precisa ser diferente da atual.'
          : null;

  const podeEnviar =
    senhaAtual.length > 0 && novaSenha.length >= MINIMO_SENHA && confirmacao === novaSenha && problema === null;

  const enviar = async (evento: FormEvent) => {
    evento.preventDefault();
    if (!podeEnviar) return;
    setTrocou(false);
    try {
      await trocar.mutateAsync({ currentPassword: senhaAtual, newPassword: novaSenha });
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmacao('');
      setTrocou(true);
    } catch {
      // A mensagem aparece pelo estado da mutação.
    }
  };

  const criadaEm = new Date(usuario.createdAt).toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="space-y-4">
      <PageHeader titulo="Perfil" voltar="/" />

      <section className="card flex items-center gap-4">
        <span
          aria-hidden
          className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-adega-wine text-2xl font-semibold text-white"
        >
          {inicial(usuario.name)}
        </span>
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold">{nomeFormatado(usuario.name)}</p>
          <p className="truncate text-sm text-adega-muted">{usuario.email}</p>
          <p className="text-xs text-adega-muted">Conta criada em {criadaEm}</p>
        </div>
      </section>

      <form className="card space-y-3" onSubmit={(evento) => void enviar(evento)}>
        <div>
          <h2 className="font-semibold">Trocar senha</h2>
          <p className="text-xs text-adega-muted">
            Os outros aparelhos conectados a esta conta saem; este continua conectado.
          </p>
        </div>

        <div>
          <label className="label" htmlFor="senha-atual">
            Senha atual
          </label>
          <input
            id="senha-atual"
            type="password"
            autoComplete="current-password"
            className="field"
            value={senhaAtual}
            onChange={(e) => {
              setSenhaAtual(e.target.value);
              setTrocou(false);
            }}
          />
        </div>

        <div>
          <label className="label" htmlFor="nova-senha">
            Nova senha
          </label>
          <input
            id="nova-senha"
            type="password"
            autoComplete="new-password"
            className="field"
            value={novaSenha}
            onChange={(e) => {
              setNovaSenha(e.target.value);
              setTrocou(false);
            }}
            placeholder={`Pelo menos ${MINIMO_SENHA} caracteres`}
          />
        </div>

        <div>
          <label className="label" htmlFor="confirmacao">
            Confirme a nova senha
          </label>
          <input
            id="confirmacao"
            type="password"
            autoComplete="new-password"
            className="field"
            value={confirmacao}
            onChange={(e) => {
              setConfirmacao(e.target.value);
              setTrocou(false);
            }}
          />
        </div>

        {problema ? <p className="text-sm text-amber-300">{problema}</p> : null}
        {trocar.error ? <ErrorBox erro={trocar.error} /> : null}
        {trocou ? (
          <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">
            Senha trocada. Os outros aparelhos foram desconectados.
          </p>
        ) : null}

        <button type="submit" className="btn-primary w-full" disabled={!podeEnviar || trocar.isPending}>
          {trocar.isPending ? 'Trocando…' : 'Trocar senha'}
        </button>
      </form>

      <Preferencias />

      {/* Só para quem administra. Esconder o atalho não é controle de acesso —
          quem garante é o servidor, que responde 404 nas rotas de convite para
          todo mundo que não é o admin. Isto aqui é só não oferecer uma porta
          que a pessoa não pode abrir. */}
      {usuario.admin ? (
        <Link to="/convites" className="card flex items-center justify-between gap-3">
          <span>
            <span className="font-semibold">Convites</span>
            <span className="block text-sm text-adega-muted">
              Quem pode criar conta no sistema
            </span>
          </span>
          <span aria-hidden className="text-adega-muted">
            ›
          </span>
        </Link>
      ) : null}

      {/* Sobre: as duas versões lado a lado.
          Frontend e backend sobem separados, e o service worker pode estar
          servindo um bundle antigo enquanto a API já é a nova. Um número só
          não denunciaria isso — dois, sim. */}
      <section className="card space-y-3">
        <h2 className="font-semibold">Sobre</h2>

        <dl className="space-y-1 text-sm">
          <div className="flex items-center justify-between gap-3">
            <dt className="text-adega-muted">App neste aparelho</dt>
            <dd className="font-mono">{VERSAO_DO_APP}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="text-adega-muted">API</dt>
            <dd className="font-mono">
              {versaoDaApi.isLoading ? '…' : (versaoDaApi.data?.version ?? 'não respondeu')}
            </dd>
          </div>
        </dl>

        {desencontroDeVersao ? (
          <div className="space-y-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            <p className="text-amber-200">
              Este aparelho está numa versão diferente da API. É o app guardado em cache
              aqui, não um problema no servidor.
            </p>
            <button type="button" className="btn-primary w-full" onClick={() => void atualizarOApp()}>
              Atualizar este aparelho
            </button>
          </div>
        ) : null}
      </section>

      {/*
        Entrada por digital.
        
        So aparece onde o navegador suporta WebAuthn, que exige contexto seguro:
        funciona em https e em localhost, e NUNCA em http://192.168.0.x -- o
        endereco usado para testar no celular pela rede local. Sumir e melhor
        que oferecer um botao que da erro ao ser tocado.
        
        E por cofre de credenciais, nao por aparelho: o Google sincroniza a
        chave entre os Androids da mesma conta, mas o Windows Hello do PC e
        outro cofre. Por isso o botao fala em "neste aparelho" -- e o texto
        explica que ela viaja junto com a conta do Google.
      */}
      {passkeySuportado() ? (
        <section className="card space-y-3">
          <div>
            <h2 className="font-semibold">Entrar com a digital</h2>
            <p className="text-sm text-adega-muted">
              Sua digital não sai do aparelho e o app nunca a recebe — ele guarda só uma chave
              pública. O Google (ou o iCloud) sincroniza a chave entre seus aparelhos; no
              computador, a senha continua valendo.
            </p>
          </div>

          {passkeys && passkeys.length > 0 ? (
            <ul className="space-y-2">
              {passkeys.map((chave) => (
                <li
                  key={chave.id}
                  className="flex items-center justify-between gap-3 rounded-xl bg-adega-surface-2 px-3 py-2 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block truncate">{chave.apelido ?? 'Aparelho'}</span>
                    <span className="block text-xs text-adega-muted">
                      {chave.lastUsedAt
                        ? `Último uso em ${new Date(chave.lastUsedAt).toLocaleDateString('pt-BR')}`
                        : 'Ainda não usada'}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn-ghost shrink-0 text-xs text-red-300/80 hover:text-red-300"
                    disabled={removerPasskey.isPending}
                    onClick={() => void removerPasskey.mutateAsync(chave.id)}
                  >
                    Remover
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {registrarPasskey.error ? <ErrorBox erro={registrarPasskey.error} /> : null}

          <button
            type="button"
            className="btn-secondary w-full"
            disabled={registrarPasskey.isPending}
            onClick={() => void registrarPasskey.mutateAsync()}
          >
            {registrarPasskey.isPending ? 'Aguarde…' : '👆 Ativar neste aparelho'}
          </button>
        </section>
      ) : null}

      <button
        type="button"
        className="btn-secondary w-full"
        disabled={logout.isPending}
        onClick={() => void logout.mutateAsync()}
      >
        Sair desta conta
      </button>

      {/*
        A porta de saída.
        
        Enquanto os convidados eram amigos, "me manda mensagem que eu apago"
        bastava. Convidando gente que não se conhece, não: quem experimenta,
        decide que não é para ela e quer sumir precisa conseguir sozinha.

        Discreto e por último, como o excluir garrafa: longe da ação principal,
        para não ser tocado por engano. A confirmação diz o que vai embora em
        números concretos -- "suas adegas e garrafas" e abstrato e facil de
        aceitar; "3 adegas e 82 garrafas" faz a pessoa parar e pensar.
      */}
      <div className="pt-4">
        {confirmandoExclusao ? (
          <div className="space-y-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm">
            <p>
              Isto apaga <strong>sua conta e tudo que é dela</strong>
              {resumo ? (
                <>
                  {' '}
                  — {resumo}
                </>
              ) : null}
              , inclusive as fotos dos rótulos. Não dá para desfazer.
            </p>
            {excluir.error ? <ErrorBox erro={excluir.error} /> : null}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                className="btn-secondary text-xs"
                onClick={() => setConfirmandoExclusao(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn bg-red-700 text-xs text-white hover:bg-red-600"
                disabled={excluir.isPending}
                onClick={() => void excluir.mutateAsync()}
              >
                {excluir.isPending ? 'Apagando…' : 'Apagar minha conta'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="btn-ghost w-full text-xs text-red-300/80 hover:text-red-300"
            onClick={() => setConfirmandoExclusao(true)}
          >
            Apagar minha conta
          </button>
        )}
      </div>
    </div>
  );
}
